import { createAsyncThunk, createSlice, SerializedError } from '@reduxjs/toolkit';
import { Billing } from "@smash-sdk/billing";
import { Vat } from '@smash-sdk/vat/ejs/10-2019';
import { AsyncState } from '../../shared/models/async.interface';
import { RootState } from '../../store';
import { BillingInformation } from './models/BillingInformation.interface';
import { CountryDisplay } from './models/CountryDisplay.interface';


interface AvailableCountriesState extends AsyncState {
    countries: CountryDisplay[]
}

interface InFormationsState extends AsyncState {
    informations: BillingInformation | null;
    isUpdated: boolean;
}

export interface BillingInformationsState {
    availableCountries: AvailableCountriesState;
    billingInformation: InFormationsState,
}

export const Currencies = {
    eur: 'eur',
    usd: 'usd',
};

export const listCountries = createAsyncThunk(
    'billingInformations/listCountries',
    async (_, thunkApi) => {
        try {
            const vat = new Vat();
            const { countries } = await vat.listCountries();

            const sorted = countries.sort((a, b) => {
                if (a.label > b.label)
                    return 1;

                if (a.label < b.label)
                    return -1;

                return 0;
            }).map(country => ({ ...country, currency: Currencies[country.currency as keyof typeof Currencies] || Currencies.usd, }));

            return { countries: sorted };
        } catch (error: any) {
            return thunkApi.rejectWithValue({ ...error });
        }
    }
)

export const getBillingInformation = createAsyncThunk(
    'billingInformations/getBillingInformation',
    async (_, thunkApi) => {
        try {
            const billing = new Billing();
            const { informations } = await billing.getInformations();
            return { billingInformation: informations };
        } catch (error: any) {
            return thunkApi.rejectWithValue({ ...error });
        }
    }
)

export const updateBillingInformation = createAsyncThunk(
    'billingInformations/updateBillingInformation',
    async (billingInformation: BillingInformation, thunkApi) => {
        try {
            const billing = new Billing();
            const { informations } = await billing.updateInformations(billingInformation);
            return { billingInformation: informations };
        } catch (error: any) {
            return thunkApi.rejectWithValue({ ...error });
        }
    }
)


export const initialState: BillingInformationsState = {
    availableCountries: {
        countries: [],
        isLoading: false,
        isSuccess: false,
        isError: false,
    },
    billingInformation: {
        informations: null,
        isLoading: false,
        isUpdated: false,
        isSuccess: false,
        error: undefined,
        isError: false,
    },
}

export const billingInformationsSlice = createSlice({
    name: 'billingInformations',
    initialState,
    reducers: {
        resetBillingInformations: (state) => ({
            availableCountries: {
                countries: [],
                isLoading: false,
                isSuccess: false,
                isError: false,
            },
            billingInformation: {
                informations: null,
                isLoading: false,
                isUpdated: false,
                isSuccess: false,
                error: undefined,
                isError: false,
            },
        }),
        resetUpdated: (state) => {
            state.billingInformation = { ...state.billingInformation, isUpdated: false }
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(listCountries.pending, (state) => ({
                ...state,
                availableCountries: {
                    countries: [],
                    isLoading: true,
                    isSuccess: false,
                    isError: false,
                    error: undefined,
                }
            }))
            .addCase(listCountries.fulfilled, (state, action) => {
                state.availableCountries.isSuccess = true;
                state.availableCountries.isLoading = false;
                state.availableCountries.countries = action.payload.countries;
            })
            .addCase(listCountries.rejected, (state) => {
                state.availableCountries.isSuccess = false;
                state.availableCountries.isLoading = false;
                state.availableCountries.isError = true;
                state.availableCountries.countries = initialState.availableCountries.countries;
            })
            .addCase(updateBillingInformation.pending, (state) => {
                state.billingInformation.isLoading = true;
                state.billingInformation.isSuccess = false;
                state.billingInformation.isUpdated = false;
                state.billingInformation.isError = false;
                state.billingInformation.error = undefined;
            })
            .addCase(updateBillingInformation.fulfilled, (state, action) => ({
                ...state,
                billingInformation: {
                    isSuccess: true,
                    isUpdated: true,
                    isLoading: false,
                    isError: false,
                    error: undefined,
                    informations: action.payload.billingInformation,
                }
            }))
            .addCase(updateBillingInformation.rejected, (state, action) => {
                state.billingInformation.isSuccess = false;
                state.billingInformation.isUpdated = false;
                state.billingInformation.isLoading = false;
                state.billingInformation.isError = true;
                state.billingInformation.error = action.payload as SerializedError;
            })
            .addCase(getBillingInformation.pending, (state) => {
                state.billingInformation.isLoading = true;
                state.billingInformation.isSuccess = false;
                state.billingInformation.isError = false;
            })
            .addCase(getBillingInformation.fulfilled, (state, action) => {
                state.billingInformation.isSuccess = true;
                state.billingInformation.isLoading = false;
                state.billingInformation.informations = action.payload.billingInformation;
            })
            .addCase(getBillingInformation.rejected, (state, action) => {
                state.billingInformation.isSuccess = false;
                state.billingInformation.isLoading = false;
                state.billingInformation.isError = true;
                state.billingInformation.error = action.payload as SerializedError;
            })
    }
});


export const { resetBillingInformations, resetUpdated } = billingInformationsSlice.actions;
export const billingInformationsState = (state: RootState) => state.billingInformations;

export default billingInformationsSlice.reducer;